import { useUpdateCurrentUser } from "@babackapp/private-api-client/client/use-update-current-user";
import type { UserDto } from "@babackapp/private-api-client/types";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@babackapp/ui/components/form";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@babackapp/ui/components/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Loading from "@/components-legacy/loading.tsx";

const formSchema = z.object({
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	lang: z.string().optional(),
});

type EditNameFormValues = z.infer<typeof formSchema>;

interface EditNameFormProps {
	user: UserDto;
}

export default function EditNameForm({ user }: EditNameFormProps) {
	const t = useTranslation();
	const updateCurrentUser = useUpdateCurrentUser();
	const form = useForm<EditNameFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			firstName: user.profile?.firstName ?? "",
			lastName: user.profile?.lastName ?? "",
			lang: user.profile?.lang ?? "fr-FR",
		},
	});

	const onSubmit = form.handleSubmit((data) => {
		updateCurrentUser.mutate({
			body: {
				firstName: data.firstName,
				lastName: data.lastName,
				lang: data.lang,
			},
		});
	});

	return (
		<Form {...form}>
			<form onSubmit={onSubmit} className={"flex flex-col gap-4"}>
				<FormField
					control={form.control}
					name={"firstName"}
					render={({ field }) => (
						<FormInput label={t("misc.firstName")} {...field} />
					)}
				/>
				<FormField
					control={form.control}
					name={"lastName"}
					render={({ field }) => (
						<FormInput label={t("misc.name")} {...field} />
					)}
				/>
				<FormField
					control={form.control}
					name={"lang"}
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t("accountSettings.language")}</FormLabel>
							<Select onValueChange={field.onChange} defaultValue={field.value}>
								<FormControl>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									<SelectItem value="fr-FR">Français</SelectItem>
									<SelectItem value="en-US">English</SelectItem>
									<SelectItem value="es-ES">Español</SelectItem>
									<SelectItem value="de-DE">Deutsch</SelectItem>
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>
				<Button type={"submit"} disabled={updateCurrentUser.isPending}>
					{updateCurrentUser.isPending ? (
						<Loading className="w-4 h-4" />
					) : (
						t("misc.save")
					)}
				</Button>
			</form>
		</Form>
	);
}
